import { z } from "zod"

const allTargetEnvs = ["demo", "development", "local", "production", "staging"] as const

export const TargetEnv = z.enum(allTargetEnvs)
export type TargetEnv = z.infer<typeof TargetEnv>

const DevTargetEnvAlias = z.enum(["dev", "develop"])
const StagingTargetEnvAlias = z.enum(["stg"])

const defaultTargetEnv: TargetEnv = "production"

export function getTargetEnv(): TargetEnv {
  if (process.env.SUPERNOVA_ENV) {
    const raw = process.env.SUPERNOVA_ENV.toLowerCase()

    const baseParseResult = TargetEnv.safeParse(raw)
    if (baseParseResult.success) return baseParseResult.data

    const devAliasParseResult = DevTargetEnvAlias.safeParse(raw)
    if (devAliasParseResult.success) return "development"

    const stgAliasParseResult = StagingTargetEnvAlias.safeParse(raw)
    if (stgAliasParseResult.success) return "staging"

    console.warn(`Incorrect value of SUPERNOVA_ENV variable, allowed options are: [${allTargetEnvs.join(",")}] `)
    console.warn(`Using defaut env instead (${defaultTargetEnv})`)
  }

  return defaultTargetEnv
}

const authServerUrlMap: Record<TargetEnv, string> = {
  demo: "https://auth-2.demo.supernova.io/api/plugin-oauth",
  development: "https://auth-2.dev.supernova.io/api/plugin-oauth",
  local: `https://auth-2.dev.supernova.io/api/plugin-oauth`,
  production: "https://auth-2.supernova.io/api/plugin-oauth",
  staging: "https://auth-2.staging.supernova.io/api/plugin-oauth",
}

export function authUrlForEnvironment(env: TargetEnv): string {
  return authServerUrlMap[env]
}

export function storybookUrlForEnvironment(env: TargetEnv): string {
  switch (env) {
    case "demo":
      return "https://storybook.demo.sst.supernova.io"
    case "development":
      return "https://storybook.dev.supernova.io"
    case "local":
      return `https://storybook.${getLocalStageName()}.sst.supernova.io`
    case "production":
      return "https://storybook.supernova.io"
    case "staging":
      return "https://storybook.staging.supernova.io"
  }
}

export function appUrlForEnvironment(env: TargetEnv): string {
  switch (env) {
    case "local":
    case "demo":
    case "development":
      return "https://app.dev.supernova.io"
    case "production":
      return "https://app.supernova.io"
    case "staging":
      return "https://app.staging.supernova.io"
  }
}

export function apiUrlForEnvironment(env: TargetEnv, version: string | null = "v2"): string {
  let url = ((): string => {
    switch (env) {
      case "demo":
        return "https://api.demo.supernova.io"
      case "development":
        return "https://api.dev.supernova.io"
      case "local":
        return `https://api-5.${getLocalStageName()}.sst.supernova.io`
      case "production":
        return "https://api.supernova.io"
      case "staging":
        return "https://api.staging.supernova.io"
    }
  })()

  if (version) {
    url += `/api/${version}`
  }

  return url
}

function getLocalStageName() {
  const parseResult = z.string().safeParse(process.env.SUPERNOVA_STAGE_NAME)
  if (!parseResult.success) {
    throw new Error(`To use local environment, SUPERNOVA_STAGE_NAME variable must be set`)
  }

  return parseResult.data
}
